import { ecLean, record, upper, type Row } from '../janus-engine/ability/context.js';
import { actorRowFor, confirmedRevFor, waitForActorMovement } from './movement-truth.js';
import { fetchAndCacheCaster, withSettledCasterReads } from './caster-cache.js';

/** One read barrier for a completion batch. An optional error map isolates failed participants in boundary drains. */
export async function ensureActivityMembers(encounterId: string, participantIds: readonly string[], readErrors?: Map<string, unknown>): Promise<Map<string, Row>> {
  const ids = [...new Set(participantIds.map(upper).filter(id => /^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(id)))];
  const baseReady = await window.ftAwaitEntityBase?.(ids);
  const held = (id: string): Row | null => record(actorRowFor(encounterId, id))
    ?? record(window.FateTide?.casterCache?.get(id));
  const missing: string[] = [];
  const reads = await Promise.allSettled(ids.map(async id => {
    await waitForActorMovement(encounterId, id);
    const member = held(id);
    if (baseReady === false || !member || !record(record(member.computed)?.creature) || !record(member.item_layer_types)
      || Number(ecLean(member, 'movement_rev')) < confirmedRevFor(encounterId, id, member as never)) {
      missing.push(id);
      await withSettledCasterReads(() => fetchAndCacheCaster(id, { skipEncounterSeed: true, throwOnError: true }));
    }
  }));
  reads.forEach((result, index) => {
    if (result.status === 'rejected') {
      if (!readErrors) throw result.reason;
      readErrors.set(ids[index]!, result.reason);
    }
  });
  const members = new Map(ids.flatMap((id, index) => { const member = reads[index]?.status === 'fulfilled' ? held(id) : null; return member ? [[id, member] as const] : []; }));
  window.dispatchEvent(new CustomEvent('ft:activity-members-ready', { detail: { encounterId, participantIds: ids, fetchedIds: missing } }));
  return members;
}
